import { useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, type GateHistory, type OpenWindow, type StandUse } from "../lib/api";
import { heatColor } from "../lib/history";
import { HowToRead } from "./HowToRead";
import { Section } from "./Section";

/**
 * The History page's Gates tab: how much of the open day each gate was in use, at which
 * hours, and the regular gaps an airline could be offered. Read from the flights kept and
 * each day's final plan, so it is what happened, not what the schedule promised.
 */

const card = "rounded-xl border border-line p-4";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-middle";
const hh = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;

/** The tab's opening line. */
export function gatesSentence(g: GateHistory | null, place: string): string {
  if (!g || g.days === 0) return "No plans were kept for these dates.";
  const busiest = g.stands[0];
  const bridge = g.windows.filter((w) => w.kind === "bridge");
  const longest = bridge[0] ?? g.windows[0];
  let s = `Over ${g.days} ${g.days === 1 ? "day" : "days"} the contact gates at ${place} were in use ${Math.round(average(g.stands.map((x) => x.usedPct)))}% of the open day (${hh(g.openFromHour)}–${hh(g.openToHour)}).`;
  if (busiest) s += ` ${busiest.name} was the busiest, at ${Math.round(busiest.usedPct)}%.`;
  if (longest) s += ` The longest regular gap is ${longest.from}–${longest.to}, when ${longest.kind === "bridge" ? "a jet-bridge gate" : "a contact gate"} was free on ${longest.days} of ${g.days} days.`;
  else s += " No gap of an hour or more was free on most days.";
  return s;
}

const average = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

export function GatesTab({ from, to, place }: { from: string; to: string; place: string }) {
  const q = useQuery({ queryKey: ["gate-history", from, to], queryFn: () => api.gateHistory(from, to) });
  if (q.isLoading) return <Loader2 className="size-5 animate-spin text-ink-3" />;
  if (q.isError) return <p className="text-[13px] text-bad">{(q.error as Error).message}</p>;
  const g = q.data ?? null;
  return (
    <>
      <HowToRead id="history.gates">
        <GatesNotes />
      </HowToRead>
      <p className="mb-4 max-w-3xl text-[14px] text-ink">{gatesSentence(g, place)}</p>
      {g && g.days > 0 && (
        <>
          <Section
            title="Where are the gaps to sell?"
            caption="Stretches of the open day when a gate was free on most days, an hour or longer: first with a jet bridge, then any contact gate an airline could be given. Each says on how many days it was free for the whole stretch, and how many gates were free on a typical day."
          >
            <Windows windows={g.windows} days={g.days} />
          </Section>

          <Section
            title="Which gates are used most?"
            caption="Share of the open day each contact gate had an aircraft on it, busiest first, with the visits it took and how long a visit lasted on average."
          >
            <StandTable stands={g.stands} />
          </Section>

          <Section
            title="When is each gate in use?"
            caption="One row per gate, one column per hour of the open day. The darker the cell, the more of that hour the gate was occupied, averaged over the days. The bottom row is the whole apron."
          >
            <UseGrid g={g} />
          </Section>
        </>
      )}
    </>
  );
}

export function GatesNotes() {
  return (
    <>
      <li>Built from the flights the app kept and each day&apos;s final plan: which gate each aircraft visit had, from arrival to departure, actual times where flown.</li>
      <li>A visit with only an arrival or only a departure holds its gate for the type&apos;s usual turn, as the board assumes.</li>
      <li>The open day is 05:00 to midnight. A gate free at 03:00 is not an offer.</li>
      <li>A gap counts when it was free on at least four days in five. Gates kept for named airlines, and stands used only by hand, are not offered.</li>
    </>
  );
}

function Windows({ windows, days }: { windows: OpenWindow[]; days: number }) {
  if (!windows.length) return <p className="text-[12.5px] text-ink-3">No stretch of an hour or more was free on most days.</p>;
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {windows.map((w) => (
        <div key={`${w.kind}${w.from}`} className={clsx(card, "flex flex-col gap-1")}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="tabular text-[16px] font-bold">{`${w.from}–${w.to}`}</span>
            <span className={clsx("rounded px-1.5 py-0.5 text-[10.5px] font-semibold", w.kind === "bridge" ? "bg-ok/15 text-ok" : "bg-info/15 text-info")}>
              {w.kind === "bridge" ? "jet bridge" : "contact gate"}
            </span>
          </div>
          <div className="text-[12px] text-ink-2">{`${Math.floor(w.minutes / 60)} h${w.minutes % 60 ? ` ${w.minutes % 60} min` : ""} · free all through on ${w.days} of ${days} days`}</div>
          <div className="text-[11.5px] text-ink-3">{`${w.free} ${w.free === 1 ? "gate" : "gates"} free on a typical day`}</div>
        </div>
      ))}
    </div>
  );
}

function StandTable({ stands }: { stands: StandUse[] }) {
  return (
    <table className="w-full max-w-3xl text-[12.5px]">
      <thead>
        <tr className="border-b border-line">
          <th className={th}>Gate</th>
          <th className={th}>Bridge</th>
          <th className={th} title="Share of 05:00 to midnight with an aircraft on it">
            In use
          </th>
          <th className={th}>Visits</th>
          <th className={th}>Average visit</th>
        </tr>
      </thead>
      <tbody>
        {stands.map((s) => (
          <tr key={s.stand} className="tabular border-b border-line">
            <td className={clsx(td, "font-semibold")}>{s.name}</td>
            <td className={clsx(td, "text-ink-2")}>{s.jetbridge ? "yes" : "no"}</td>
            <td className={td}>
              <span className="flex items-center gap-1.5">
                <span className="w-12 shrink-0">{`${Math.round(s.usedPct)}%`}</span>
                <span className="h-1.5 w-28 shrink-0 rounded-full bg-panel-2">
                  <span className="block h-full rounded-full bg-info" style={{ width: `${Math.min(100, s.usedPct)}%` }} />
                </span>
              </span>
            </td>
            <td className={td}>{s.turns}</td>
            <td className={td}>{s.avgVisitMin == null ? "—" : `${s.avgVisitMin} min`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UseGrid({ g }: { g: GateHistory }) {
  const [at, setAt] = useState<{ row: string; hour: number; v: number } | null>(null);
  const hours = Array.from({ length: g.openToHour - g.openFromHour }, (_, i) => g.openFromHour + i);
  const cols = `5.5rem repeat(${hours.length}, minmax(0, 1fr))`;
  const rows = [...g.stands.map((s) => ({ key: s.stand, name: s.name, byHour: s.byHour })), { key: "apron", name: "All gates", byHour: g.apronByHour }];
  return (
    <div className={card} onMouseLeave={() => setAt(null)}>
      <div className="mb-2 text-right text-[11.5px] text-ink-3">{at ? `${at.row}, ${hh(at.hour)}–${hh(at.hour + 1)}: in use ${Math.round(at.v)}% of the hour` : "Hover a cell for its figure"}</div>
      <div className="scroll-thin overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid gap-px text-[10px] text-ink-3" style={{ gridTemplateColumns: cols }}>
            <span />
            {hours.map((h) => (
              <span key={h} className="text-center">
                {h % 3 === 0 ? String(h % 24).padStart(2, "0") : ""}
              </span>
            ))}
          </div>
          {rows.map((r) => (
            <div key={r.key} className={clsx("mt-px grid gap-px", r.key === "apron" && "mt-2 border-t border-line pt-2")} style={{ gridTemplateColumns: cols }}>
              <span className={clsx("truncate pr-1 text-[11px] leading-[18px]", r.key === "apron" ? "font-semibold" : "text-ink-2")}>{r.name}</span>
              {r.byHour.map((v, i) => (
                <span
                  key={i}
                  data-cell
                  className="h-[18px] rounded-[3px]"
                  style={{ background: heatColor({ v, level: v / 100 }) }}
                  title={`${r.name}, ${hh(hours[i]!)}: in use ${Math.round(v)}%`}
                  onMouseEnter={() => setAt({ row: r.name, hour: hours[i]!, v })}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
